import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { LocalizationProvider } from "@mui/lab";
import DateAdapter from "@mui/lab/AdapterDateFns";
import { CssBaseline } from "@mui/material";
import { enGB, fi } from "date-fns/locale";
import { SnackbarProvider } from "notistack";
import * as React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { Provider } from "react-redux";
import { localstorageVersion, msalConfig } from "~/config";
import { AxiosProvider } from "~/providers/AxiosProvider";
import { store } from "~/redux/store/store";
import "~/template/css/base.css";
import PaperBaseThemeProvider from "~/template/PaperBaseThemeProvider";
import "../translations/i18n";
import AuthWrapper from "./AuthWrapper";
import { NodeonContextWrapper } from "./NodeonContextWrapper";

export interface LocaleMap {
  [key: string]: Locale;
}

export const localeMap: LocaleMap = {
  en: enGB,
  fi,
};

const App = (): JSX.Element => {
  const msalInstance = React.useRef(new PublicClientApplication(msalConfig));
  const queryClient = React.useRef(new QueryClient());
  const { i18n } = useTranslation();

  useEffect(() => {
    if (localstorageVersion !== localStorage.getItem("version")) {
      Object.keys(localStorage).forEach((key) => {
        if (key === "i18nextLng") return;
        localStorage.removeItem(key);
      });

      localStorage.setItem("version", localstorageVersion);
    }
  }, []);

  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <QueryClientProvider client={queryClient.current}>
        <MsalProvider instance={msalInstance.current}>
          <Provider store={store}>
            <AxiosProvider>
              <PaperBaseThemeProvider>
                <CssBaseline />
                <LocalizationProvider
                  dateAdapter={DateAdapter}
                  locale={localeMap[i18n.language] ?? fi}
                >
                  <NodeonContextWrapper>
                    <AuthWrapper />
                  </NodeonContextWrapper>
                </LocalizationProvider>
              </PaperBaseThemeProvider>
            </AxiosProvider>
          </Provider>
        </MsalProvider>
        <ReactQueryDevtools />
      </QueryClientProvider>
    </SnackbarProvider>
  );
};

export default App;
